import React, { useEffect, useState } from "react";
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  MenuItem,
  Select,
  Box,
} from "@mui/material";
import { Visibility, Delete, Edit, Search, Add } from "@mui/icons-material";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  listWards,
  createWard,
  updateWard,
  deleteWard,
} from "../../../api/bedManagement";

const WardList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  // Pagination States
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalWards, setTotalWards] = useState(0);

  // Debounce search query to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1); // Reset to first page on new search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Wards with Pagination
  const { data, refetch, isLoading, isError } = useQuery({
    queryKey: [
      "wards-list",
      { search: debouncedSearch, page, page_size: rowsPerPage },
    ],
    queryFn: () =>
      listWards({ search: debouncedSearch || undefined, page, page_size: rowsPerPage }, token),
  });

  // Dialog and Snackbar States
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedWard, setSelectedWard] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [form, setForm] = useState({ name: "", code: "", capacity: 0 });

  // Handlers for Update Dialog
  const handleOpenUpdateDialog = (ward) => {
    setSelectedWard(ward);
    setForm({
      name: ward ? ward.name : "",
      code: ward ? ward.code : "",
      capacity: ward ? ward.capacity : 0,
    });
    setOpenUpdateDialog(true);
  };

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false);
    setSelectedWard(null);
    setForm({ name: "", code: "", capacity: 0 });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedWard) {
        await updateWard(selectedWard.id, form, token);
        setSnackbarMessage(
          t("bedManagement.ward_updated", {
            defaultValue: "Ward updated successfully",
          })
        );
      } else {
        await createWard(form, token);
        setSnackbarMessage(
          t("bedManagement.ward_created", {
            defaultValue: "Ward created successfully",
          })
        );
      }
      refetch();
      handleCloseUpdateDialog();
    } catch (error) {
      console.error("Error saving ward:", error);
      setSnackbarMessage(
        t("bedManagement.error_saving", { defaultValue: "Error saving ward" })
      );
    }
  };

  const handleDelete = async (wardId) => {
    try {
      await deleteWard(wardId, token);
      setSnackbarMessage(
        t("bedManagement.ward_deleted", {
          defaultValue: "Ward deleted successfully",
        })
      );
      refetch();
    } catch (error) {
      console.error("Error deleting ward:", error);
      setSnackbarMessage(
        t("bedManagement.error_deleting", {
          defaultValue: "Error deleting ward",
        })
      );
    }
  };

  // Calculate total wards from response
  useEffect(() => {
    if (data) {
      const total = Array.isArray(data) ? data.length : data?.count || 0;
      setTotalWards(total);
    }
  }, [data]);

  return (
    <Grid item xs={12}>
      <Paper sx={{ boxShadow: "none", m: "1em 3em" }}>
        <Grid container padding={"20px"}>
          {/* Header Section with Search Bar */}
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {t("bedManagement.wards.title", {
                defaultValue: "Wards Management",
              })}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpenUpdateDialog(null)}
                sx={{ textTransform: "capitalize" }}
              >
                {t("bedManagement.add_ward", { defaultValue: "Add Ward" })}
              </Button>
              <TextField
                placeholder={t("bedManagement.search", {
                  defaultValue: "Search wards...",
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: "300px" }}
              />
            </Box>
          </Grid>
          <Divider sx={{ mt: 1, width: "100%" }} />

          {/* Loading, Error, or Data Display */}
          {isLoading ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Grid>
          ) : isError ? (
            <Grid container justifyContent="center" sx={{ mt: 4 }}>
              <Typography color="error">
                {t("bedManagement.failedToLoad", {
                  defaultValue: "Failed to load wards",
                })}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => refetch()}
                sx={{ mt: 2 }}
              >
                {t("bedManagement.retry", { defaultValue: "Retry" })}
              </Button>
            </Grid>
          ) : (
            <>
              <TableContainer sx={{ mt: 4 }}>
                <Table sx={{ minWidth: "100%" }} aria-label="wards table">
                  <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
                    <TableRow>
                      <TableCell align="center">
                        {t("bedManagement.wards.id", { defaultValue: "ID" })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.wards.name", {
                          defaultValue: "Name",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.wards.code", {
                          defaultValue: "Code",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.wards.capacity", {
                          defaultValue: "Capacity",
                        })}
                      </TableCell>
                      <TableCell align="center">
                        {t("bedManagement.actions", {
                          defaultValue: "Actions",
                        })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                    {(Array.isArray(data) ? data : data?.results || [])?.length > 0 ? (
                      (Array.isArray(data) ? data : data?.results || []).map((ward) => (
                        <TableRow
                          key={ward.id}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center">{ward.id}</TableCell>
                          <TableCell align="center">{ward.name}</TableCell>
                          <TableCell align="center">{ward.code}</TableCell>
                          <TableCell align="center">{ward.capacity}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleOpenUpdateDialog(ward)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(ward.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            {t("bedManagement.no_wards", {
                              defaultValue: "No wards found",
                            })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
                flexWrap="wrap"
                gap={2}
              >
                {/* Rows Per Page Selector */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    {t("bedManagement.rows_per_page", "Rows Per Page")}:
                  </Typography>
                  <FormControl variant="standard" sx={{ minWidth: 80 }}>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Pagination Component */}
                {totalWards > rowsPerPage && (
                  <Pagination
                    count={Math.ceil(totalWards / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                )}
              </Box>
            </>
          )}
        </Grid>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedWard
            ? t("bedManagement.wards.edit", { defaultValue: "Edit Ward" })
            : t("bedManagement.wards.add", { defaultValue: "Add Ward" })}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.wards.name", {
                    defaultValue: "Name",
                  })}
                  value={form.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, name: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("bedManagement.wards.code", {
                    defaultValue: "Code",
                  })}
                  value={form.code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, code: e.target.value }))
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="number"
                  label={t("bedManagement.wards.capacity", {
                    defaultValue: "Capacity",
                  })}
                  value={form.capacity}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      capacity: parseInt(e.target.value) || 0,
                    }))
                  }
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUpdateDialog}>
              {t("common.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" variant="contained">
              {selectedWard
                ? t("common.update", { defaultValue: "Update" })
                : t("common.create", { defaultValue: "Create" })}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
    </Grid>
  );
};

export default WardList;
